import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Text } from '@mantine/core';

import { SuggestionPanel, SuggestionRow } from './flowDocumentEditor.styled';
import type {
	FieldSuggestionListHandle,
	FieldSuggestionListProps,
} from './flowDocumentEditor.types';

/**
 * The list `{{` opens.
 *
 * Keyboard first: the caret is in the document, so the author's hands are on the keys and
 * reaching for the mouse to pick a field would be slower than typing the token by hand — which
 * is the thing this exists to replace.
 */
export const FieldSuggestionList = forwardRef<FieldSuggestionListHandle, FieldSuggestionListProps>(
	({ items, command, query }, ref) => {
		const [activeIndex, setActiveIndex] = useState(0);

		// The list is re-filtered on every keystroke, so an index held from the previous query
		// can point past the end — or at a different field than the one that was highlighted.
		useEffect(() => setActiveIndex(0), [query]);

		useImperativeHandle(ref, () => ({
			onKeyDown: (event: KeyboardEvent): boolean => {
				if (items.length === 0) {
					return false;
				}
				if (event.key === 'ArrowDown') {
					setActiveIndex((previous) => (previous + 1) % items.length);
					return true;
				}
				if (event.key === 'ArrowUp') {
					setActiveIndex((previous) => (previous - 1 + items.length) % items.length);
					return true;
				}
				if (event.key === 'Enter' || event.key === 'Tab') {
					const item = items[activeIndex];
					if (item === undefined) {
						return false;
					}
					command(item);
					return true;
				}
				return false;
			},
		}));

		if (items.length === 0) {
			return (
				<SuggestionPanel data-testid="field-suggestion-empty">
					<Text size="xs" c="dimmed" p="xs">
						No data point matches “{query}”.
					</Text>
				</SuggestionPanel>
			);
		}

		return (
			<SuggestionPanel data-testid="field-suggestion-list">
				{items.map((item, index) => (
					<SuggestionRow
						key={item.name}
						type="button"
						$active={index === activeIndex}
						data-testid={`field-suggestion-${item.name}`}
						data-active={index === activeIndex}
						// Pointer down, not click: a click would first move focus out of the
						// editor, and the suggestion plugin tears the list down on blur.
						onMouseDown={(event) => {
							event.preventDefault();
							command(item);
						}}
						onMouseEnter={() => setActiveIndex(index)}
					>
						<Text size="sm">{item.label}</Text>
						{/* The group, and the value it resolves to — not the token. Two data
						    points can share a label ("First name", investor and representative)
						    and the group is the only thing that tells them apart. */}
						<Text size="xs" c="dimmed">
							{[item.group, item.sample].filter(Boolean).join(' · ')}
						</Text>
					</SuggestionRow>
				))}
			</SuggestionPanel>
		);
	}
);

FieldSuggestionList.displayName = 'FieldSuggestionList';
